import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Folder, File, HardDrive, ArrowLeft, ArrowUp, ArrowClockwise,
  FolderPlus, Trash, PencilSimple, CircleNotch,
  CaretRight, Desktop, Warning, FileText, FileImage, FileZip,
  FilePdf, FileCode, FileAudio, FileVideo, FileCsv, Database,
  FolderOpen, GearSix, ArrowRight,
  House, Monitor, Laptop, CloudArrowUp
} from '@phosphor-icons/react';

/* ─── icon helper ─── */
const EXT_MAP = {
  image: ['jpg','jpeg','png','gif','bmp','svg','webp','ico'],
  pdf: ['pdf'],
  zip: ['zip','rar','7z','tar','gz','bz2'],
  code: ['js','ts','py','java','cpp','c','h','cs','php','rb','go','rs','html','css','xml','json','yaml','yml','ps1','bat','cmd','sh'],
  audio: ['mp3','wav','ogg','flac','aac','wma','m4a'],
  video: ['mp4','avi','mkv','mov','wmv','flv','webm'],
  csv: ['csv','tsv'],
  db: ['db','sqlite','sqlite3','mdb','accdb'],
  exe: ['exe','msi','dll','sys'],
  text: ['txt','log','md','ini','cfg','conf','reg'],
};
const ICONS = {
  folder: { I: Folder, c: 'text-yellow-400' },
  drive: { I: HardDrive, c: 'text-blue-400' },
  image: { I: FileImage, c: 'text-pink-400' },
  pdf: { I: FilePdf, c: 'text-red-400' },
  zip: { I: FileZip, c: 'text-orange-400' },
  code: { I: FileCode, c: 'text-green-400' },
  audio: { I: FileAudio, c: 'text-purple-400' },
  video: { I: FileVideo, c: 'text-cyan-400' },
  csv: { I: FileCsv, c: 'text-emerald-400' },
  db: { I: Database, c: 'text-amber-400' },
  exe: { I: GearSix, c: 'text-red-500' },
  text: { I: FileText, c: 'text-[#a1a1aa]' },
  default: { I: File, c: 'text-[#a1a1aa]' },
};
const fileType = (name, isDir, mode) => {
  if (isDir) return mode === 'drive' ? 'drive' : 'folder';
  const ext = (name || '').split('.').pop().toLowerCase();
  for (const [t, exts] of Object.entries(EXT_MAP)) if (exts.includes(ext)) return t;
  return 'default';
};
const fmtSize = (b) => {
  if (!b) return '-';
  const u = ['B','KB','MB','GB','TB']; let i = 0; let s = b;
  while (s >= 1024 && i < u.length - 1) { s /= 1024; i++; }
  return `${s.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

/* ─── Breadcrumb ─── */
const Breadcrumb = ({ path, onNav, label, icon: Ico }) => {
  const isDrives = !path || path === 'DRIVES';
  const parts = isDrives ? [] : path.replace(/\\/g, '/').split('/').filter(Boolean);
  return (
    <div className="flex items-center gap-1 text-xs flex-wrap min-h-[20px]">
      <button onClick={() => onNav('DRIVES')} className="text-[#D50C2D] hover:text-white transition-colors font-medium flex items-center gap-1">
        <Ico size={14} />{label}
      </button>
      {parts.map((p, i) => {
        const nav = parts.slice(0, i + 1).join('\\') + '\\';
        return (
          <span key={i} className="flex items-center gap-1">
            <CaretRight size={10} className="text-[#3f3f46]" />
            <button onClick={() => onNav(nav)} className={`hover:text-white transition-colors ${i === parts.length - 1 ? 'text-white font-medium' : 'text-[#71717a]'}`}>{p}</button>
          </span>
        );
      })}
    </div>
  );
};

/* ─── date formatter ─── */
const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d).replace('T', ' ');
  return s.length >= 16 ? s.slice(0, 16) : s;
};

/* ─── File Row ─── */
const FileRow = ({ item, selected, onSelect, onDblClick }) => {
  const ft = fileType(item.name, item.is_dir, item.mode);
  const { I: Icon, c: color } = ICONS[ft] || ICONS.default;
  return (
    <div
      className={`grid grid-cols-[28px_1fr_110px_70px] gap-1 px-3 py-1.5 border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors cursor-pointer group text-xs ${selected ? 'bg-[#D50C2D]/10 border-[#D50C2D]/20' : ''}`}
      onClick={() => onSelect(item.name)}
      onDoubleClick={() => onDblClick(item)}
    >
      <div className="flex items-center"><Icon size={16} weight={item.is_dir ? 'fill' : 'regular'} className={color} /></div>
      <div className="flex items-center min-w-0">
        <span className={`truncate ${item.is_dir ? 'text-white font-medium' : 'text-[#a1a1aa]'}`}>{item.name}</span>
        {item.driveType && <span className="ml-2 text-[9px] text-[#3f3f46] shrink-0">{item.label || item.driveType}</span>}
      </div>
      <div className="text-right text-[#3f3f46] flex items-center justify-end tabular-nums">{fmtDate(item.modified)}</div>
      <div className="text-right text-[#52525b] flex items-center justify-end">{item.mode === 'drive' ? fmtSize(item.size) : item.is_dir ? '-' : fmtSize(item.size)}</div>
    </div>
  );
};

/* ─── Panel Toolbar ─── */
const PanelToolbar = ({ onBack, onUp, onRefresh, canBack, isRoot, loading }) => (
  <div className="flex items-center gap-1 px-2 py-1 border-b border-white/5 bg-[#0d0d0f]">
    <button onClick={onBack} disabled={canBack === false || loading} className="p-1 rounded hover:bg-white/5 text-[#71717a] hover:text-white disabled:opacity-20 transition-colors"><ArrowLeft size={14} /></button>
    <button onClick={onUp} disabled={isRoot || loading} className="p-1 rounded hover:bg-white/5 text-[#71717a] hover:text-white disabled:opacity-20 transition-colors"><ArrowUp size={14} /></button>
    <button onClick={onRefresh} disabled={loading} className="p-1 rounded hover:bg-white/5 text-[#71717a] hover:text-white disabled:opacity-20 transition-colors"><ArrowClockwise size={14} className={loading ? 'animate-spin' : ''} /></button>
  </div>
);

/* ═══════════════════════════════════════════════════════
   useRemotePanel – Reusable hook for a single remote panel
   ═══════════════════════════════════════════════════════ */
function useRemotePanel() {
  const [deviceId, setDeviceId] = useState('');
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState('DRIVES');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState(['DRIVES']);
  const [histIdx, setHistIdx] = useState(0);
  const [pendingTaskId, setPendingTaskId] = useState(null);

  const wsRef = useRef(null);
  const wsTimeoutRef = useRef(null);
  const wsFallbackRef = useRef(null);
  const pollRef = useRef(null);
  const browseResolvedRef = useRef(false);
  const currentTaskIdRef = useRef(null);
  const retryRef = useRef(0);
  const pendingPathRef = useRef('');
  const [wsConnected, setWsConnected] = useState(false);

  const parseAndSetFiles = useCallback((output) => {
    if (!output) return false;
    const trimmed = output.trim();
    if (trimmed.startsWith('ERROR:')) {
      setError(trimmed);
      return false;
    }
    try {
      const parsed = JSON.parse(trimmed);
      const items = Array.isArray(parsed) ? parsed : [parsed];
      items.sort((a, b) => {
        if (a.is_dir && !b.is_dir) return -1;
        if (!a.is_dir && b.is_dir) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setFiles(items);
      setError(null);
      return true;
    } catch {
      setError('Ungültige Antwort vom Agent');
      return false;
    }
  }, []);

  /* WebSocket connection with keepalive ping */
  useEffect(() => {
    if (!deviceId) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/fm/${deviceId}`;

    let ws;
    let reconnectTimer;
    let pingInterval;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          // Keepalive ping every 20s to prevent connection drops
          pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ action: 'ping' })); } catch {}
            }
          }, 20000);
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'fm_result') {
              browseResolvedRef.current = true;
              if (wsTimeoutRef.current) clearTimeout(wsTimeoutRef.current);
              if (wsFallbackRef.current) clearTimeout(wsFallbackRef.current);
              if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
              setPendingTaskId(null);
              currentTaskIdRef.current = null;
              retryRef.current = 0;
              parseAndSetFiles(msg.output);
              setLoading(false);
            } else if (msg.type === 'task_created') {
              currentTaskIdRef.current = msg.task_id || null;
              // Start HTTP fallback polling after 30s if WS doesn't deliver
              wsFallbackRef.current = setTimeout(() => {
                if (!browseResolvedRef.current && currentTaskIdRef.current) {
                  setPendingTaskId(currentTaskIdRef.current);
                }
              }, 30000);
            } else if (msg.type === 'pong') {
              // keepalive response, ignore
            }
          } catch {}
        };

        ws.onclose = () => {
          setWsConnected(false);
          wsRef.current = null;
          if (pingInterval) clearInterval(pingInterval);
          reconnectTimer = setTimeout(connect, 2000);
        };

        ws.onerror = () => {
          setWsConnected(false);
        };
      } catch {}
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingInterval) clearInterval(pingInterval);
      if (wsTimeoutRef.current) clearTimeout(wsTimeoutRef.current);
      if (wsFallbackRef.current) clearTimeout(wsFallbackRef.current);
      if (ws) { ws.onclose = null; ws.close(); }
      wsRef.current = null;
    };
  }, [deviceId, parseAndSetFiles]);

  /* Browse with auto-retry: WS first, then HTTP fallback */
  const browse = useCallback(async (p) => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    setFiles([]);
    setSelected(new Set());
    setPath(p);
    browseResolvedRef.current = false;
    retryRef.current = 0;

    const sendBrowse = () => {
      // Try WebSocket first
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: 'browse', path: p }));
        // Timeout: 35s per attempt, then retry via WS or fall back to HTTP
        wsTimeoutRef.current = setTimeout(() => {
          if (browseResolvedRef.current) return;
          retryRef.current += 1;
          if (retryRef.current < 3) {
            // Auto-retry via WS
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ action: 'browse', path: p }));
              wsTimeoutRef.current = setTimeout(() => {
                if (browseResolvedRef.current) return;
                // Final fallback: try HTTP
                (async () => {
                  try {
                    const res = await api.post('/filemanager/browse', { device_id: deviceId, path: p });
                    setPendingTaskId(res.data.task_id);
                  } catch (e) {
                    if (!browseResolvedRef.current) {
                      setError('Agent nicht erreichbar. Bitte prüfe ob der Agent auf dem Gerät läuft.');
                      setLoading(false);
                    }
                  }
                })();
              }, 35000);
            }
          } else {
            setError('Agent antwortet nicht nach 3 Versuchen. Bitte prüfe ob der Agent auf dem Gerät läuft und versuche es erneut.');
            setLoading(false);
          }
        }, 35000);
        return;
      }

      // No WS available: use HTTP directly
      (async () => {
        try {
          const res = await api.post('/filemanager/browse', { device_id: deviceId, path: p });
          setPendingTaskId(res.data.task_id);
        } catch (e) {
          setError(e.response?.data?.detail || 'Verbindungsfehler');
          setLoading(false);
        }
      })();
    };

    pendingPathRef.current = p;
    sendBrowse();
  }, [deviceId]);

  /* HTTP Polling fallback (activated when WS doesn't deliver or WS unavailable) */
  useEffect(() => {
    if (!pendingTaskId) return;
    if (browseResolvedRef.current) { setPendingTaskId(null); return; }
    const startTime = Date.now();
    const poll = async () => {
      if (browseResolvedRef.current) {
        setPendingTaskId(null);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        return;
      }
      if (Date.now() - startTime > 120000) {
        setPendingTaskId(null);
        if (!browseResolvedRef.current) {
          setError('Agent antwortet nicht (Timeout 120s). Agent möglicherweise beschäftigt - bitte erneut versuchen.');
          setLoading(false);
        }
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        return;
      }
      try {
        const res = await api.get(`/filemanager/task/${pendingTaskId}`);
        const d = res.data;
        if (d.status === 'completed' || d.status === 'partial') {
          browseResolvedRef.current = true;
          setPendingTaskId(null);
          parseAndSetFiles(d.output);
          setLoading(false);
        } else if (d.status === 'cancelled') {
          setPendingTaskId(null);
          setError('Task abgebrochen');
          setLoading(false);
        }
      } catch (_) {}
    };
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [pendingTaskId, parseAndSetFiles]);

  const nav = useCallback((p) => {
    const newHist = [...history.slice(0, histIdx + 1), p];
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
    browse(p);
  }, [history, histIdx, browse]);

  const goUp = () => {
    if (path === 'DRIVES') return;
    const norm = path.replace(/\\/g, '/').replace(/\/$/, '');
    const parts = norm.split('/').filter(Boolean);
    if (parts.length <= 1) nav('DRIVES');
    else nav(parts.slice(0, -1).join('\\') + '\\');
  };

  const goBack = () => {
    if (histIdx > 0) {
      const i = histIdx - 1;
      setHistIdx(i);
      browse(history[i]);
    }
  };

  const dblClick = (item) => {
    if (!item.is_dir) return;
    let p;
    if (item.mode === 'drive') p = item.name;
    else if (path === 'DRIVES') p = item.name;
    else p = path.replace(/[/\\]$/, '') + '\\' + item.name + '\\';
    nav(p);
  };

  const fullPath = (name) => {
    if (path === 'DRIVES') return name;
    return path.replace(/[/\\]$/, '') + '\\' + name;
  };

  const toggleSelect = (name) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(name) ? s.delete(name) : s.add(name);
      return s;
    });
  };

  const selectDevice = useCallback((id, devices) => {
    setDeviceId(id);
    setDeviceInfo(devices.find(d => d.id === id) || null);
    setPath('DRIVES');
    setFiles([]);
    setHistory(['DRIVES']);
    setHistIdx(0);
    setSelected(new Set());
    setError(null);
  }, []);

  useEffect(() => {
    if (!deviceId) return;
    (async () => {
      try {
        const res = await api.get(`/filemanager/cache/${deviceId}`);
        if (res.data.drives && res.data.drives.length > 0) {
          setFiles(res.data.drives);
          setPath('DRIVES');
          setLoading(false);
        }
      } catch {}
    })();
    browse('DRIVES');
  }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    deviceId, deviceInfo, files, path, loading, error, selected,
    histIdx, wsConnected,
    browse, nav, goUp, goBack, dblClick, fullPath, toggleSelect, selectDevice,
    setSelected,
  };
}

/* ═══════════════════════════════════════════════════════
   useLocalPanel – Hook for browser-local file system browsing
   ═══════════════════════════════════════════════════════ */
function useLocalPanel() {
  const [dirHandle, setDirHandle] = useState(null);
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  // Stack of directory handles for navigation
  const [handleStack, setHandleStack] = useState([]);

  const readDir = useCallback(async (handle, displayPath) => {
    setLoading(true);
    setError(null);
    setFiles([]);
    setSelected(new Set());
    try {
      const entries = [];
      for await (const entry of handle.values()) {
        const item = {
          name: entry.name,
          is_dir: entry.kind === 'directory',
          mode: 'local',
          _handle: entry,
        };
        if (entry.kind === 'file') {
          try {
            const f = await entry.getFile();
            item.size = f.size;
            item.modified = f.lastModified ? new Date(f.lastModified).toISOString().slice(0, 16) : '';
          } catch {}
        }
        entries.push(item);
      }
      entries.sort((a, b) => {
        if (a.is_dir && !b.is_dir) return -1;
        if (!a.is_dir && b.is_dir) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setFiles(entries);
      setPath(displayPath);
    } catch (e) {
      setError('Fehler beim Lesen: ' + e.message);
    }
    setLoading(false);
  }, []);

  const openFolder = useCallback(async () => {
    try {
      if (!window.showDirectoryPicker) {
        setError('Browser unterstützt kein File System Access API. Bitte Chrome/Edge verwenden.');
        return;
      }
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
      setDirHandle(handle);
      setHandleStack([handle]);
      const p = handle.name;
      setHistory([{ handle, path: p }]);
      setHistIdx(0);
      readDir(handle, p);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError('Ordner konnte nicht geöffnet werden');
      }
    }
  }, [readDir]);

  const dblClick = useCallback(async (item) => {
    if (!item.is_dir || !item._handle) return;
    const newPath = path ? path + '/' + item.name : item.name;
    const newStack = [...handleStack, item._handle];
    setHandleStack(newStack);
    const newHist = [...history.slice(0, histIdx + 1), { handle: item._handle, path: newPath }];
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
    readDir(item._handle, newPath);
  }, [path, handleStack, history, histIdx, readDir]);

  const goUp = useCallback(() => {
    if (handleStack.length <= 1) return;
    const newStack = handleStack.slice(0, -1);
    const parentHandle = newStack[newStack.length - 1];
    const parts = path.split('/');
    parts.pop();
    const parentPath = parts.join('/');
    setHandleStack(newStack);
    const newHist = [...history.slice(0, histIdx + 1), { handle: parentHandle, path: parentPath }];
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
    readDir(parentHandle, parentPath);
  }, [handleStack, path, history, histIdx, readDir]);

  const goBack = useCallback(() => {
    if (histIdx <= 0) return;
    const i = histIdx - 1;
    setHistIdx(i);
    const entry = history[i];
    if (entry) {
      readDir(entry.handle, entry.path);
    }
  }, [histIdx, history, readDir]);

  const refresh = useCallback(() => {
    if (history[histIdx]) {
      readDir(history[histIdx].handle, history[histIdx].path);
    }
  }, [histIdx, history, readDir]);

  const toggleSelect = (name) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(name) ? s.delete(name) : s.add(name);
      return s;
    });
  };

  // Read a file as base64 for transfer
  const readFileBase64 = useCallback(async (name) => {
    const item = files.find(f => f.name === name);
    if (!item || !item._handle || item.is_dir) return null;
    try {
      const file = await item._handle.getFile();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = reader.result.split(',')[1];
          resolve(b64);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });
    } catch { return null; }
  }, [files]);

  return {
    dirHandle, files, path, loading, error, selected,
    histIdx,
    openFolder, dblClick, goUp, goBack, refresh, toggleSelect, readFileBase64,
    setSelected,
  };
}

/* ═══════════════════════════════════════════════════════
   LocalBrowsePanel – Renders the local file system panel
   ═══════════════════════════════════════════════════════ */
function LocalBrowsePanel({ panel }) {
  const { dirHandle, files, path, loading, error, selected, histIdx, openFolder, dblClick, goUp, goBack, refresh, toggleSelect } = panel;

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {!dirHandle ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6">
          <div className="w-20 h-20 rounded-full bg-[#141416] border border-white/5 flex items-center justify-center">
            <Laptop size={36} className="text-blue-400" />
          </div>
          <div className="text-center">
            <p className="text-white font-medium mb-1">Lokalen Ordner öffnen</p>
            <p className="text-[#52525b] text-xs max-w-[220px] mb-4">Wähle einen Ordner auf diesem Computer um Dateien zu durchsuchen</p>
            <Button onClick={openFolder} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="fm-open-local-folder">
              <FolderOpen size={16} className="mr-2" />Ordner öffnen
            </Button>
          </div>
          {error && <p className="text-red-400 text-xs text-center mt-2">{error}</p>}
        </div>
      ) : (
        <>
          <PanelToolbar onBack={goBack} onUp={goUp} onRefresh={refresh}
            canBack={histIdx > 0} isRoot={!path || path.indexOf('/') === -1} loading={loading} />

          <div className="px-3 py-1 border-b border-white/5 bg-[#0d0d0f] flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs flex-wrap min-h-[20px]">
              <Laptop size={14} className="text-blue-400" />
              {path.split('/').filter(Boolean).map((p, i, arr) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <CaretRight size={10} className="text-[#3f3f46]" />}
                  <span className={i === arr.length - 1 ? 'text-white font-medium' : 'text-[#71717a]'}>{p}</span>
                </span>
              ))}
            </div>
            <button onClick={openFolder} className="p-1 rounded hover:bg-white/5 text-[#52525b] hover:text-blue-400 transition-colors" title="Anderen Ordner öffnen">
              <FolderOpen size={14} />
            </button>
          </div>

          <div className="grid grid-cols-[28px_1fr_110px_70px] gap-1 px-3 py-1 bg-[#0A0A0B] border-b border-white/5 text-[10px] text-[#3f3f46] uppercase tracking-wider">
            <div /><div>Name</div><div className="text-right">Datum</div><div className="text-right">Größe</div>
          </div>

          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <CircleNotch size={28} className="animate-spin text-blue-400" />
            </div>
          )}

          {!loading && error && (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 p-4">
              <Warning size={28} className="text-red-400" />
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="flex-1 overflow-y-auto">
                {files.length === 0 && (
                  <div className="py-8 text-center text-[#3f3f46] text-xs">Ordner ist leer</div>
                )}
                {files.map((item, i) => (
                  <FileRow key={item.name + i} item={item} selected={selected.has(item.name)}
                    onSelect={toggleSelect} onDblClick={dblClick} />
                ))}
              </div>
              <div className="px-3 py-1 bg-[#0A0A0B] border-t border-white/5 text-[10px] text-[#3f3f46]">
                {files.length} Einträge {selected.size > 0 && <span className="text-blue-400">| {selected.size} ausgewählt</span>}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   RemoteBrowsePanel – Renders a single remote browsing panel
   ═══════════════════════════════════════════════════════ */
function RemoteBrowsePanel({ panel, devices, side, label, icon: PanelIcon, accentClass, onMkdir, onRename, onDelete }) {
  const { deviceId, deviceInfo, files, path, loading, error, selected, histIdx, browse, nav, goUp, goBack, dblClick, toggleSelect } = panel;

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Panel Header with device selector */}
      <div className="px-3 py-2 bg-[#111113] border-b border-white/5 flex items-center gap-2">
        <PanelIcon size={16} className={accentClass} />
        <span className="text-white text-sm font-semibold">{label}</span>
        {deviceInfo && (
          <span className="text-[10px] text-[#3f3f46] ml-1">({deviceInfo.computer_name})</span>
        )}
        <div className="ml-auto">
          <Select value={deviceId || '__none__'} onValueChange={(v) => { if (v !== '__none__') panel.selectDevice(v, devices); }}>
            <SelectTrigger className="w-[180px] h-7 bg-[#141416] border-white/10 text-white text-xs" data-testid={`fm-device-selector-${side}`}>
              <SelectValue placeholder="Gerät wählen..." />
            </SelectTrigger>
            <SelectContent className="bg-[#141416] border-white/10">
              <SelectItem value="__none__" className="text-[#52525b] text-xs">Gerät wählen...</SelectItem>
              {devices.filter(d => d.is_online).map(d => (
                <SelectItem key={d.id} value={d.id} className="text-white text-xs hover:bg-white/5">
                  <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400" />{d.computer_name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!deviceId ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6">
          <div className="w-20 h-20 rounded-full bg-[#141416] border border-white/5 flex items-center justify-center">
            <Desktop size={36} className="text-[#3f3f46]" />
          </div>
          <div className="text-center">
            <p className="text-white font-medium mb-1">Gerät auswählen</p>
            <p className="text-[#52525b] text-xs max-w-[200px]">Wähle ein Online-Gerät um Laufwerke und Dateien zu durchsuchen</p>
          </div>
        </div>
      ) : (
        <>
          <PanelToolbar onBack={goBack} onUp={goUp} onRefresh={() => browse(path)}
            canBack={histIdx > 0} isRoot={path === 'DRIVES'} loading={loading} />

          <div className="px-3 py-1 border-b border-white/5 bg-[#0d0d0f] flex items-center justify-between">
            <Breadcrumb path={path} onNav={nav} label={deviceInfo?.computer_name || 'Remote'} icon={HardDrive} />
            <div className="flex items-center gap-1">
              {path !== 'DRIVES' && (
                <button onClick={() => onMkdir(panel)}
                  className="p-1 rounded hover:bg-white/5 text-[#52525b] hover:text-green-400 transition-colors" title="Neuer Ordner">
                  <FolderPlus size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-[28px_1fr_110px_70px] gap-1 px-3 py-1 bg-[#0A0A0B] border-b border-white/5 text-[10px] text-[#3f3f46] uppercase tracking-wider">
            <div /><div>Name</div><div className="text-right">Datum</div><div className="text-right">Größe</div>
          </div>

          {loading && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4">
              <CircleNotch size={28} className="animate-spin text-[#D50C2D]" />
              <div className="text-center">
                <p className="text-white text-sm">Warte auf Agent...</p>
                <p className="text-[#3f3f46] text-xs mt-1">Der Agent auf dem Gerät muss den Befehl ausführen</p>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4">
              <Warning size={28} className="text-red-400" />
              <p className="text-red-400 text-sm text-center">{error}</p>
              <button onClick={() => browse(path)} className="text-xs text-[#D50C2D] hover:text-white transition-colors">Erneut versuchen</button>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="flex-1 overflow-y-auto">
                {files.length === 0 && (
                  <div className="py-8 text-center text-[#3f3f46] text-xs">
                    {path === 'DRIVES' ? 'Laufwerke werden geladen...' : 'Verzeichnis ist leer'}
                  </div>
                )}
                {files.map((item, i) => (
                  <div key={item.name + i} className="group relative">
                    <FileRow item={item} selected={selected.has(item.name)}
                      onSelect={toggleSelect}
                      onDblClick={dblClick} />
                    {item.mode !== 'drive' && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); onRename(panel, item); }}
                          className="p-0.5 rounded hover:bg-white/10 text-[#52525b] hover:text-yellow-400"><PencilSimple size={12} /></button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(panel, item); }}
                          className="p-0.5 rounded hover:bg-white/10 text-[#52525b] hover:text-red-400"><Trash size={12} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-3 py-1 bg-[#0A0A0B] border-t border-white/5 text-[10px] text-[#3f3f46]">
                {files.length} Einträge {selected.size > 0 && <span className="text-[#D50C2D]">| {selected.size} ausgewählt</span>}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function FileManagerPage() {
  const { deviceId: paramDeviceId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [devices, setDevices] = useState([]);

  /* Left panel mode: 'local' or 'remote' */
  const [leftMode, setLeftMode] = useState('local');

  /* Two independent remote panels + one local panel */
  const leftRemotePanel = useRemotePanel();
  const localPanel = useLocalPanel();
  const rightPanel = useRemotePanel();

  /* Transfer state */
  const [transferring, setTransferring] = useState(false);
  const [transferMsg, setTransferMsg] = useState('');

  /* Dialog state */
  const [mkdirOpen, setMkdirOpen] = useState(false);
  const [mkdirName, setMkdirName] = useState('');
  const [mkdirPanel, setMkdirPanel] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePanel, setDeletePanel] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renamePanel, setRenamePanel] = useState(null);

  /* ─── Load devices ─── */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/devices', { params: { per_page: 500 } });
        const devs = res.data.devices || [];
        setDevices(devs);
        if (paramDeviceId) {
          setLeftMode('remote');
          leftRemotePanel.selectDevice(paramDeviceId, devs);
        }
      } catch (_) {}
    })();
  }, [paramDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ═══ TRANSFER: Local → Right (upload local files to remote device) ═══ */
  const transferLocalToRight = async () => {
    if (localPanel.selected.size === 0) { toast.error('Keine lokalen Dateien ausgewählt'); return; }
    if (!rightPanel.deviceId) { toast.error('Bitte zuerst ein Ziel-Gerät rechts wählen'); return; }
    if (rightPanel.path === 'DRIVES') { toast.error('Bitte zuerst einen Ordner auf der rechten Seite öffnen'); return; }

    setTransferring(true);
    let successCount = 0;

    for (const name of localPanel.selected) {
      const item = localPanel.files.find(f => f.name === name);
      if (!item || item.is_dir) continue;

      setTransferMsg(`Lese lokal: ${name}...`);
      try {
        const b64 = await localPanel.readFileBase64(name);
        if (!b64) { toast.error(`${name}: Konnte nicht gelesen werden`); continue; }

        setTransferMsg(`Upload zu ${rightPanel.deviceInfo?.computer_name}: ${name}...`);
        const remoteDest = rightPanel.path.replace(/[/\\]$/, '') + '\\' + name;
        await api.post('/filemanager/upload', {
          device_id: rightPanel.deviceId,
          path: remoteDest,
          data_base64: b64,
        });
        successCount++;
      } catch (e) {
        toast.error(`${name}: ${e.response?.data?.detail || e.message}`);
      }
    }

    setTransferring(false);
    setTransferMsg('');
    if (successCount > 0) {
      toast.success(`${successCount} Datei(en) hochgeladen → ${rightPanel.deviceInfo?.computer_name}`);
      setTimeout(() => rightPanel.browse(rightPanel.path), 3000);
    }
  };

  /* ═══ TRANSFER: Right → Local (download from remote, save via browser) ═══ */
  const transferRightToLocal = async () => {
    if (rightPanel.selected.size === 0) { toast.error('Keine Dateien auf der rechten Seite ausgewählt'); return; }

    setTransferring(true);
    let successCount = 0;

    for (const name of rightPanel.selected) {
      const item = rightPanel.files.find(f => f.name === name);
      if (!item || item.is_dir) continue;

      setTransferMsg(`Download von ${rightPanel.deviceInfo?.computer_name}: ${name}...`);
      try {
        const dlRes = await api.post('/filemanager/download', {
          device_id: rightPanel.deviceId,
          path: rightPanel.fullPath(name),
        });
        const taskId = dlRes.data.task_id;

        let fileData = null;
        const startTime = Date.now();
        while (Date.now() - startTime < 120000) {
          await new Promise(r => setTimeout(r, 2000));
          const r = await api.get(`/filemanager/task/${taskId}`);
          if (r.data.status === 'completed' || r.data.status === 'partial') {
            if (r.data.output && !r.data.output.startsWith('ERROR:')) {
              fileData = r.data.output.trim();
            } else {
              toast.error(`${name}: ${r.data.output || 'Fehler'}`);
            }
            break;
          } else if (r.data.status === 'cancelled') {
            toast.error(`${name}: Abgebrochen`);
            break;
          }
        }

        if (!fileData) continue;

        // Trigger browser download
        const byteStr = atob(fileData);
        const bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
        const blob = new Blob([bytes]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        successCount++;
      } catch (e) {
        toast.error(`${name}: ${e.response?.data?.detail || e.message}`);
      }
    }

    setTransferring(false);
    setTransferMsg('');
    if (successCount > 0) {
      toast.success(`${successCount} Datei(en) heruntergeladen`);
    }
  };

  /* ═══ TRANSFER: Remote Left → Right ═══ */
  const transferRemoteLeftToRight = async () => {
    if (leftRemotePanel.selected.size === 0) { toast.error('Keine Dateien auf der linken Seite ausgewählt'); return; }
    if (!rightPanel.deviceId) { toast.error('Bitte zuerst ein Ziel-Gerät rechts wählen'); return; }
    if (rightPanel.path === 'DRIVES') { toast.error('Bitte zuerst einen Ordner auf der rechten Seite öffnen'); return; }

    setTransferring(true);
    let successCount = 0;

    for (const name of leftRemotePanel.selected) {
      const item = leftRemotePanel.files.find(f => f.name === name);
      if (!item || item.is_dir) continue;

      setTransferMsg(`Download von ${leftRemotePanel.deviceInfo?.computer_name}: ${name}...`);
      try {
        const dlRes = await api.post('/filemanager/download', {
          device_id: leftRemotePanel.deviceId,
          path: leftRemotePanel.fullPath(name),
        });
        const taskId = dlRes.data.task_id;

        let fileData = null;
        const startTime = Date.now();
        while (Date.now() - startTime < 120000) {
          await new Promise(r => setTimeout(r, 2000));
          const r = await api.get(`/filemanager/task/${taskId}`);
          if (r.data.status === 'completed' || r.data.status === 'partial') {
            if (r.data.output && !r.data.output.startsWith('ERROR:')) {
              fileData = r.data.output.trim();
            } else {
              toast.error(`${name}: ${r.data.output || 'Fehler'}`);
            }
            break;
          } else if (r.data.status === 'cancelled') {
            toast.error(`${name}: Abgebrochen`);
            break;
          }
        }

        if (!fileData) continue;

        setTransferMsg(`Upload zu ${rightPanel.deviceInfo?.computer_name}: ${name}...`);
        const remoteDest = rightPanel.path.replace(/[/\\]$/, '') + '\\' + name;
        await api.post('/filemanager/upload', {
          device_id: rightPanel.deviceId,
          path: remoteDest,
          data_base64: fileData,
        });
        successCount++;
      } catch (e) {
        toast.error(`${name}: ${e.response?.data?.detail || e.message}`);
      }
    }

    setTransferring(false);
    setTransferMsg('');
    if (successCount > 0) {
      toast.success(`${successCount} Datei(en): ${leftRemotePanel.deviceInfo?.computer_name} → ${rightPanel.deviceInfo?.computer_name}`);
      setTimeout(() => rightPanel.browse(rightPanel.path), 3000);
    }
  };

  /* ═══ TRANSFER: Right → Remote Left ═══ */
  const transferRightToRemoteLeft = async () => {
    if (rightPanel.selected.size === 0) { toast.error('Keine Dateien auf der rechten Seite ausgewählt'); return; }
    if (!leftRemotePanel.deviceId) { toast.error('Bitte zuerst ein Gerät links wählen'); return; }
    if (leftRemotePanel.path === 'DRIVES') { toast.error('Bitte zuerst einen Ordner auf der linken Seite öffnen'); return; }

    setTransferring(true);
    let successCount = 0;

    for (const name of rightPanel.selected) {
      const item = rightPanel.files.find(f => f.name === name);
      if (!item || item.is_dir) continue;

      setTransferMsg(`Download von ${rightPanel.deviceInfo?.computer_name}: ${name}...`);
      try {
        const dlRes = await api.post('/filemanager/download', {
          device_id: rightPanel.deviceId,
          path: rightPanel.fullPath(name),
        });
        const taskId = dlRes.data.task_id;

        let fileData = null;
        const startTime = Date.now();
        while (Date.now() - startTime < 120000) {
          await new Promise(r => setTimeout(r, 2000));
          const r = await api.get(`/filemanager/task/${taskId}`);
          if (r.data.status === 'completed' || r.data.status === 'partial') {
            if (r.data.output && !r.data.output.startsWith('ERROR:')) {
              fileData = r.data.output.trim();
            } else {
              toast.error(`${name}: ${r.data.output || 'Fehler'}`);
            }
            break;
          } else if (r.data.status === 'cancelled') {
            toast.error(`${name}: Abgebrochen`);
            break;
          }
        }

        if (!fileData) continue;

        setTransferMsg(`Upload zu ${leftRemotePanel.deviceInfo?.computer_name}: ${name}...`);
        const remoteDest = leftRemotePanel.path.replace(/[/\\]$/, '') + '\\' + name;
        await api.post('/filemanager/upload', {
          device_id: leftRemotePanel.deviceId,
          path: remoteDest,
          data_base64: fileData,
        });
        successCount++;
      } catch (e) {
        toast.error(`${name}: ${e.response?.data?.detail || e.message}`);
      }
    }

    setTransferring(false);
    setTransferMsg('');
    if (successCount > 0) {
      toast.success(`${successCount} Datei(en): ${rightPanel.deviceInfo?.computer_name} → ${leftRemotePanel.deviceInfo?.computer_name}`);
      setTimeout(() => leftRemotePanel.browse(leftRemotePanel.path), 3000);
    }
  };

  /* ═══ Transfer dispatcher based on left mode ═══ */
  const handleTransferToRight = () => {
    if (leftMode === 'local') transferLocalToRight();
    else transferRemoteLeftToRight();
  };

  const handleTransferToLeft = () => {
    if (leftMode === 'local') transferRightToLocal();
    else transferRightToRemoteLeft();
  };

  const leftHasSelection = leftMode === 'local' ? localPanel.selected.size > 0 : leftRemotePanel.selected.size > 0;
  const leftReady = leftMode === 'local' ? !!localPanel.dirHandle : !!leftRemotePanel.deviceId;

  /* ═══ Remote Operations ═══ */
  const handleMkdir = (panel) => { setMkdirPanel(panel); setMkdirName(''); setMkdirOpen(true); };
  const doMkdir = async () => {
    if (!mkdirName.trim() || !mkdirPanel?.deviceId) return;
    const p = mkdirPanel.path.replace(/[/\\]$/, '') + '\\' + mkdirName.trim();
    try {
      await api.post('/filemanager/mkdir', { device_id: mkdirPanel.deviceId, path: p });
      toast.info('Ordner-Erstellung gesendet');
      setMkdirOpen(false); setMkdirName('');
      setTimeout(() => mkdirPanel.browse(mkdirPanel.path), 3000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDelete = (panel, item) => { setDeletePanel(panel); setDeleteTarget(item); setDeleteOpen(true); };
  const doDelete = async () => {
    if (!deleteTarget || !deletePanel?.deviceId) return;
    try {
      await api.post('/filemanager/delete', { device_id: deletePanel.deviceId, path: deletePanel.fullPath(deleteTarget.name) });
      toast.info('Löschauftrag gesendet');
      setDeleteOpen(false); setDeleteTarget(null);
      setTimeout(() => deletePanel.browse(deletePanel.path), 3000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleRename = (panel, item) => { setRenamePanel(panel); setRenameTarget(item); setRenameName(item.name); setRenameOpen(true); };
  const doRename = async () => {
    if (!renameTarget || !renameName.trim() || !renamePanel?.deviceId) return;
    try {
      await api.post('/filemanager/rename', { device_id: renamePanel.deviceId, path: renamePanel.fullPath(renameTarget.name), new_name: renameName.trim() });
      toast.info('Umbenennung gesendet');
      setRenameOpen(false); setRenameTarget(null);
      setTimeout(() => renamePanel.browse(renamePanel.path), 3000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  /* ═══ Device picker (if no device selected on either side) ═══ */
  if (!leftRemotePanel.deviceId && !rightPanel.deviceId && !localPanel.dirHandle) {
    const onlineDevs = devices.filter(d => d.is_online);
    const offlineDevs = devices.filter(d => !d.is_online);
    return (
      <div className="space-y-6" data-testid="filemanager-page">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3"><FolderOpen size={28} className="text-[#D50C2D]" />Dateimanager</h1>
        <p className="text-[#a1a1aa]">Wähle ein Online-Gerät um den Dateimanager zu starten:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {onlineDevs.map(d => (
            <button key={d.id} onClick={() => { setLeftMode('remote'); leftRemotePanel.selectDevice(d.id, devices); }} data-testid={`fm-device-${d.id}`}
              className="p-4 bg-[#141416] border border-white/5 rounded-lg hover:border-[#D50C2D]/50 transition-all text-left group">
              <div className="flex items-center gap-3 mb-2">
                <Desktop size={24} className="text-[#D50C2D]" />
                <span className="text-white font-medium group-hover:text-[#D50C2D] transition-colors">{d.computer_name}</span>
                <span className="ml-auto w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
              </div>
              <div className="text-xs text-[#52525b]">{d.location_city && <span>{d.location_city} </span>}<span className="uppercase">{d.tenant_code}</span></div>
            </button>
          ))}
          {offlineDevs.map(d => (
            <div key={d.id} className="p-4 bg-[#141416] border border-white/5 rounded-lg opacity-30 cursor-not-allowed">
              <div className="flex items-center gap-3"><Desktop size={24} className="text-[#52525b]" /><span className="text-[#71717a]">{d.computer_name}</span><span className="ml-auto w-2 h-2 rounded-full bg-red-400" /></div>
              <div className="text-xs text-[#3f3f46] mt-1">Offline</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ═══ DUAL PANE LAYOUT ═══ */
  return (
    <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="filemanager-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#0d0d0f] border-b border-white/5 shrink-0">
        <div className="flex items-center gap-3">
          <FolderOpen size={22} className="text-[#D50C2D]" />
          <span className="text-white font-bold text-lg">Dateimanager</span>
          <span className="text-[10px] text-[#3f3f46]">Dual-Pane</span>
        </div>
      </div>

      {/* Dual pane container */}
      <div className="flex flex-1 min-h-0">

        {/* ── LEFT PANEL ── */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-white/5">
          {/* Tab switcher: Lokal | Remote */}
          <div className="flex bg-[#0A0A0B] border-b border-white/5 shrink-0">
            <button
              onClick={() => setLeftMode('local')}
              data-testid="fm-tab-local"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold transition-all ${
                leftMode === 'local'
                  ? 'text-blue-400 bg-blue-500/10 border-b-2 border-blue-400'
                  : 'text-[#52525b] hover:text-[#a1a1aa] hover:bg-white/[0.02]'
              }`}
            >
              <Laptop size={14} />Lokal
            </button>
            <button
              onClick={() => setLeftMode('remote')}
              data-testid="fm-tab-remote"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold transition-all ${
                leftMode === 'remote'
                  ? 'text-[#D50C2D] bg-[#D50C2D]/10 border-b-2 border-[#D50C2D]'
                  : 'text-[#52525b] hover:text-[#a1a1aa] hover:bg-white/[0.02]'
              }`}
            >
              <Monitor size={14} />Remote
            </button>
          </div>

          {/* Panel content based on mode */}
          {leftMode === 'local' ? (
            <LocalBrowsePanel panel={localPanel} />
          ) : (
            <RemoteBrowsePanel
              panel={leftRemotePanel}
              devices={devices}
              side="left"
              label="Gerät A"
              icon={Monitor}
              accentClass="text-blue-400"
              onMkdir={handleMkdir}
              onRename={handleRename}
              onDelete={handleDelete}
            />
          )}
        </div>

        {/* ── CENTER: Transfer buttons ── */}
        <div className="w-[60px] flex flex-col items-center justify-center gap-3 bg-[#0d0d0f] shrink-0">
          <button
            onClick={handleTransferToRight}
            disabled={!leftHasSelection || !rightPanel.deviceId || rightPanel.path === 'DRIVES' || transferring}
            className="w-10 h-10 rounded-lg bg-[#D50C2D]/10 border border-[#D50C2D]/30 flex items-center justify-center text-[#D50C2D] hover:bg-[#D50C2D]/20 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            title={leftMode === 'local' ? 'Lokale Dateien hochladen' : 'Nach rechts übertragen'}
            data-testid="fm-transfer-to-right"
          >
            {transferring ? <CircleNotch size={18} className="animate-spin" /> : leftMode === 'local' ? <CloudArrowUp size={18} weight="bold" /> : <ArrowRight size={18} weight="bold" />}
          </button>
          <div className="text-[8px] text-[#3f3f46] uppercase tracking-widest" style={{ writingMode: 'vertical-lr' }}>Transfer</div>
          <button
            onClick={handleTransferToLeft}
            disabled={rightPanel.selected.size === 0 || !leftReady || transferring}
            className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 hover:bg-blue-500/20 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            title={leftMode === 'local' ? 'Dateien herunterladen' : 'Nach links übertragen'}
            data-testid="fm-transfer-to-left"
          >
            {transferring ? <CircleNotch size={18} className="animate-spin" /> : <ArrowLeft size={18} weight="bold" />}
          </button>
        </div>

        {/* ── RIGHT PANEL ── */}
        <RemoteBrowsePanel
          panel={rightPanel}
          devices={devices}
          side="right"
          label="Gerät B"
          icon={Monitor}
          accentClass="text-[#D50C2D]"
          onMkdir={handleMkdir}
          onRename={handleRename}
          onDelete={handleDelete}
        />
      </div>

      {/* Transfer status bar */}
      {transferMsg && (
        <div className="px-4 py-2 bg-[#D50C2D]/10 border-t border-[#D50C2D]/20 flex items-center gap-2 shrink-0">
          <CircleNotch size={14} className="animate-spin text-[#D50C2D]" />
          <span className="text-white text-xs">{transferMsg}</span>
        </div>
      )}

      {/* ═══ DIALOGS ═══ */}
      <Dialog open={mkdirOpen} onOpenChange={setMkdirOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderPlus size={20} className="text-green-400" />Neuer Ordner</DialogTitle></DialogHeader>
          <Input value={mkdirName} onChange={e => setMkdirName(e.target.value)} placeholder="Ordnername" className="bg-[#0A0A0B] border-white/10 text-white"
            onKeyDown={e => e.key === 'Enter' && doMkdir()} autoFocus data-testid="fm-mkdir-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMkdirOpen(false)} className="border-white/10 text-white hover:bg-white/5">Abbrechen</Button>
            <Button onClick={doMkdir} disabled={!mkdirName.trim()} className="bg-[#D50C2D] hover:bg-[#b50a26] text-white" data-testid="fm-mkdir-confirm">Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PencilSimple size={20} className="text-yellow-400" />Umbenennen</DialogTitle></DialogHeader>
          <p className="text-[#71717a] text-sm">Aktuell: <span className="text-white font-mono text-xs">{renameTarget?.name}</span></p>
          <Input value={renameName} onChange={e => setRenameName(e.target.value)} placeholder="Neuer Name" className="bg-[#0A0A0B] border-white/10 text-white"
            onKeyDown={e => e.key === 'Enter' && doRename()} autoFocus data-testid="fm-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} className="border-white/10 text-white hover:bg-white/5">Abbrechen</Button>
            <Button onClick={doRename} disabled={!renameName.trim()} className="bg-[#D50C2D] hover:bg-[#b50a26] text-white" data-testid="fm-rename-confirm">Umbenennen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash size={20} className="text-red-400" />Löschen</DialogTitle></DialogHeader>
          <p className="text-[#71717a]">Möchtest du <span className="text-white font-mono text-xs">{deleteTarget?.name}</span> wirklich löschen?</p>
          {deleteTarget?.is_dir && <p className="text-red-400 text-xs flex items-center gap-1"><Warning size={14} />Ordner und alle Inhalte werden gelöscht!</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="border-white/10 text-white hover:bg-white/5">Abbrechen</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fm-delete-confirm">Löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
